import type { GridApiPro, GridPaginationModel } from '@mui/x-data-grid-pro';
import type { UnmeteredEventsReportItem } from '@vy/types';
import { userMessage } from '../../../../../utils/vy-toasts/vy-toast';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { VyTableLite } from '../../../../../components/new-design/table/vy-table/VyTable';
import { buildUnmeteredEventsColumns } from './UnmeteredEventsReportTableColumns';

const DEFAULT_PAGINATION_MODEL: GridPaginationModel = { page: 0, pageSize: 100 };

type Props = {
  rows: UnmeteredEventsReportItem[];
  filterFieldNames: string[];
  isLoading: boolean;
  apiRef: React.MutableRefObject<GridApiPro>;
  onExportReady: (exportFn: () => void) => void;
};

export const UnmeteredEventsReportTable: React.FC<Props> = ({
  rows,
  filterFieldNames,
  isLoading,
  apiRef,
  onExportReady,
}) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(DEFAULT_PAGINATION_MODEL);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<Record<string, boolean>>({ lastRawData: false });

  const columns = useMemo(() => buildUnmeteredEventsColumns(filterFieldNames), [filterFieldNames]);

  const stableGetRowId = useCallback(
    (row: UnmeteredEventsReportItem) => `${row.customerAlias}-${row.eventName}`,
    [],
  );

  const handleExport = useCallback(() => {
    if (!apiRef.current?.exportDataAsCsv) {
      userMessage('error', 'CSV export unavailable');
      return;
    }
    const fileName = `Unmetered Events ${new Date().toISOString().slice(0, 10)}`;
    apiRef.current.exportDataAsCsv({ fileName });
    userMessage('success', 'Download complete');
  }, [apiRef]);

  useEffect(() => {
    onExportReady(handleExport);
  }, [handleExport, onExportReady]);

  return (
    <VyTableLite
      items={rows}
      columns={columns}
      loadingItems={isLoading}
      getRowId={stableGetRowId}
      apiRef={apiRef}
      density="compact"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={setColumnVisibilityModel}
    />
  );
};
